import invariant from 'invariant';
import { isNumber } from 'lodash';
import type {
  Asset,
  Collection,
  Project,
} from '@frameio/graphql-client/generated';
import { getPathnameFromHref } from '../helpers/getPathnameFromHref';
import type { ValidHref } from '../types';
// @todo (WEB-1045): Add condensed UUID support to routes
// import { toCondensedUUID } from '@frameio/utils/formatters/uuid';

// @todo (WEB-1045): Remove when condensed UUID support is added to routes
const toCondensedUUID = (uuid: string): string => uuid;

export type CollectionGetLinkParams =
  /**
   * CollectionPage and cooresponding Viewer Page
   * Redirects to the workspaces page for the given account.
   * /project/:projectId/collection/:collectionId(/view/:assetId)
   */
  | {
      page: 'collection';
      projectId: Project['id'];
      assetId?: Asset['id'];
      collectionId: Collection['id'];
      versionId?: string;
      commentId?: string;
      compare?: string[];
      activeIndex?: number;
    }
  | {
      page: 'collectionViewer';
      projectId: Project['id'];
      assetId: Asset['id'];
      collectionId: Collection['id'];
      versionId?: string;
      commentId?: string;
      compare?: string[];
      activeIndex?: number;
    };

export const getLinkForCollection = (
  params: CollectionGetLinkParams
): ValidHref => {
  const { projectId, collectionId, assetId } = params;

  // Assert required params for better error messages
  invariant(projectId, 'projectId is required');
  invariant(collectionId, 'collectionId is required');

  // /project/:projectId/collection/:collectionId(/view/:assetId)
  const _projectId = toCondensedUUID(projectId);
  const _collectionId = toCondensedUUID(collectionId);
  const _assetId = assetId ? toCondensedUUID(assetId) : undefined;

  const pathname: ValidHref = `/project/${_projectId}/collection/${_collectionId}${
    _assetId ? `/view/${_assetId}` : ''
  }`;
  let newUrl = pathname;

  const urlSearchParams = new URLSearchParams();

  // ... ?c=:commentId
  if ('commentId' in params && params.commentId) {
    urlSearchParams.set('c', toCondensedUUID(params.commentId));
  }

  // ... ?versionId=:versionId
  if ('versionId' in params && params.versionId) {
    urlSearchParams.set('version', toCondensedUUID(params.versionId));
  }

  // ... ?activeIndex=:activeIndex
  if ('activeIndex' in params && isNumber(params.activeIndex)) {
    urlSearchParams.set('activeIndex', params.activeIndex.toString());
  }

  // ... ?compare=:comparisonId1&compare=comparisonId2
  if ('compare' in params && params.compare) {
    params.compare.forEach((param) => {
      urlSearchParams.append('compare', toCondensedUUID(param));
    });
  }

  if (urlSearchParams.toString()) {
    newUrl += `?${
      urlSearchParams.toString() as {
        c?: string;
        version?: string;
        compare?: string;
        activeIndex?: string;
      }
    }`;
  }
  return newUrl as ValidHref;
};

export const isCollectionPageType = (href: ValidHref): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');
  return (
    parts.length <= 7 && parts[1] === 'project' && parts[3] === 'collection'
  );
};

export const isCollectionViewerPageType = (
  href: ValidHref | string
): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');
  return (
    parts.length <= 7 &&
    parts[1] === 'project' &&
    parts[3] === 'collection' &&
    parts.includes('view')
  );
};
